import { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ToastProvider } from './components/Toast'
import { DialogProvider } from './components/Dialogs'
import { controllingRoutes } from './features/controlling/routes'
import useFeaturesStore from './store/featuresStore'
import useAuthStore from './store/authStore'
import { canSeeCompanyDashboard, isManagerUser } from './services/users'
// Componenti sempre necessari: eager. Le pagine: lazy (code-splitting,
// PERF-005) così il main bundle non include ogni rotta al primo caricamento.
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import OnboardingBanner from './components/OnboardingBanner'
import ErrorBoundary from './components/ErrorBoundary'
import LoginPage from './pages/LoginPage'
import Loading from './components/Loading'

const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const ProjectsList = lazy(() => import('./pages/projects/ProjectsList'))
const ProjectForm = lazy(() => import('./pages/projects/ProjectForm'))
const ProjectDetail = lazy(() => import('./pages/projects/ProjectDetail'))
const CompetenzaOverview = lazy(() => import('./pages/projects/CompetenzaOverview'))
const SignatureReport = lazy(() => import('./pages/projects/SignatureReport'))
const DirezioneDashboard = lazy(() => import('./pages/dashboard/DirezioneDashboard'))
const EstimatesList = lazy(() => import('./pages/estimates/EstimatesList'))
const EstimateForm = lazy(() => import('./pages/estimates/EstimateForm'))
const Parcella = lazy(() => import('./pages/estimates/Parcella'))
const ParcelleList = lazy(() => import('./pages/estimates/ParcelleList'))
const EstimateDetail = lazy(() => import('./pages/estimates/EstimateDetail'))
const OpportunitiesList = lazy(() => import('./pages/opportunities/OpportunitiesList'))
const OpportunityDetail = lazy(() => import('./pages/opportunities/OpportunityDetail'))
const ClientsList = lazy(() => import('./pages/clients/ClientsList'))
const ClientDetail = lazy(() => import('./pages/clients/ClientDetail'))
const ClientsMap = lazy(() => import('./pages/clients/ClientsMap'))
const TimesheetsList = lazy(() => import('./pages/timesheets/TimesheetsList'))
const TimesheetForm = lazy(() => import('./pages/timesheets/TimesheetForm'))
const PendingApprovals = lazy(() => import('./pages/timesheets/PendingApprovals'))
const CollaboratorsList = lazy(() => import('./pages/collaborators/CollaboratorsList'))
const CollaboratorDetail = lazy(() => import('./pages/collaborators/CollaboratorDetail'))
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'))
const MyDashboard = lazy(() => import('./pages/dashboard/MyDashboard'))
const PMDashboard = lazy(() => import('./pages/dashboard/PMDashboard'))
const Calendar = lazy(() => import('./pages/calendar/Calendar'))
const TeamLoad = lazy(() => import('./pages/resources/TeamLoad'))
const Coefficients = lazy(() => import('./pages/estimates/Coefficients'))
const ClassifyLines = lazy(() => import('./pages/estimates/ClassifyLines'))
const MyAgenda = lazy(() => import('./pages/appointments/MyAgenda'))
const AppointmentDetail = lazy(() => import('./pages/appointments/AppointmentDetail'))
const Offices = lazy(() => import('./pages/appointments/Offices'))
const SopralluoghiReport = lazy(() =>
  import('./pages/sopralluoghi/SopralluoghiReport'),
)
const ProjectMargins = lazy(() => import('./pages/dashboard/ProjectMargins'))
const InvoicesList = lazy(() => import('./pages/invoices/InvoicesList'))
const InvoiceForm = lazy(() => import('./pages/invoices/InvoiceForm'))
const InvoiceDetail = lazy(() => import('./pages/invoices/InvoiceDetail'))
const InvoiceSchedule = lazy(() => import('./pages/invoices/InvoiceSchedule'))
const BankReconciliation = lazy(() => import('./pages/invoices/BankReconciliation'))
const Cashflow = lazy(() => import('./pages/invoices/Cashflow'))
const ReceivedInvoices = lazy(() => import('./pages/invoices/ReceivedInvoices'))
const PMRequests = lazy(() => import('./pages/requests/PMRequests'))
const Timeline = lazy(() => import('./pages/projects/Timeline'))
const Leaves = lazy(() => import('./pages/leaves/Leaves'))
const CsvImport = lazy(() => import('./pages/clients/CsvImport'))
const Profile = lazy(() => import('./pages/profile/Profile'))
const UsersAdmin = lazy(() => import('./pages/admin/UsersAdmin'))
const TaxSettings = lazy(() => import('./pages/admin/TaxSettings'))
const StudioSettings = lazy(() => import('./pages/admin/StudioSettings'))
const AuditLog = lazy(() => import('./pages/admin/AuditLog'))
const DataImport = lazy(() => import('./pages/admin/DataImport'))
const TeamStats = lazy(() => import('./pages/admin/TeamStats'))
const LineTemplates = lazy(() => import('./pages/admin/LineTemplates'))
const PaymentTerms = lazy(() => import('./pages/admin/PaymentTerms'))
const GoogleCalendar = lazy(() => import('./pages/admin/GoogleCalendar'))
const Notifications = lazy(() => import('./pages/admin/Notifications'))

export default function App() {
  return (
    <ToastProvider>
    <DialogProvider>
    <BrowserRouter>
      <ErrorBoundary>
      <Suspense
        fallback={<Loading />}
      >
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          <Route path="/dashboard" element={<DashboardSwitch />} />
          <Route path="/pm" element={<PMDashboard />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/agenda" element={<MyAgenda />} />
          <Route path="/appointments/:id" element={<AppointmentDetail />} />
          {/* Agenda dello studio confluita nella vista Giorno del calendario;
              la rotta resta e reindirizza per non rompere i segnalibri */}
          <Route path="/agenda-studio" element={<Navigate to="/calendar?view=day" replace />} />
          <Route path="/sedi" element={<Offices />} />
          <Route path="/rimborsi-km" element={<SopralluoghiReport />} />
          {/* vecchio path: la voce di menu si chiamava "Rimborsi km" ma
              portava qui, un URL senza relazione col nome (punto n2 della
              checklist di raffinamento) */}
          <Route path="/sopralluoghi" element={<Navigate to="/rimborsi-km" replace />} />
          <Route path="/analytics/margins" element={<ProjectMargins />} />
          <Route path="/timeline" element={<Timeline />} />

          <Route path="/lavorazioni-anno" element={<CompetenzaOverview />} />
          <Route path="/carico-team" element={<TeamLoad />} />
          <Route path="/coefficienti" element={<Coefficients />} />
          <Route path="/coefficienti/classifica" element={<ClassifyLines />} />
          <Route path="/compensi-firma" element={<SignatureReport />} />
          <Route path="/cruscotto" element={<DirezioneDashboard />} />
          <Route path="/leaves" element={<Leaves />} />
          <Route path="/projects" element={<ProjectsList />} />
          <Route path="/projects/new" element={<ProjectForm />} />
          <Route path="/projects/:id" element={<ProjectDetail />} />
          <Route path="/projects/:id/edit" element={<ProjectForm />} />

          <Route path="/opportunities" element={<OpportunitiesList />} />
          <Route path="/opportunities/:id" element={<OpportunityDetail />} />
          <Route path="/estimates" element={<EstimatesList />} />
          <Route path="/estimates/new" element={<EstimateForm />} />
          {/* le attività preliminari sono ora incontri: il vecchio indirizzo porta al calendario */}
          <Route path="/attivita" element={<Navigate to="/calendar" replace />} />
          <Route path="/estimates/parcelle" element={<ParcelleList />} />
          <Route path="/estimates/parcella" element={<Parcella />} />
          <Route path="/estimates/parcella/:id" element={<Parcella />} />
          <Route path="/estimates/:id" element={<EstimateDetail />} />
          <Route path="/estimates/:id/edit" element={<EstimateForm />} />

          <Route path="/clients" element={<ClientsList />} />
          <Route path="/clients/map" element={<ClientsMap />} />
          <Route path="/clients/:id" element={<ClientDetail />} />
          <Route path="/clients/import" element={<CsvImport />} />

          <Route path="/timesheets" element={<TimesheetsList />} />
          <Route path="/timesheets/new" element={<TimesheetForm />} />
          <Route path="/timesheets/:id/edit" element={<TimesheetForm />} />
          <Route path="/timesheets/pending" element={<PendingApprovals />} />

          <Route path="/collaborators" element={<CollaboratorsList />} />
          <Route path="/collaborators/:id" element={<CollaboratorDetail />} />

          <Route path="/invoices" element={<InvoicesList />} />
          <Route path="/invoices/schedule" element={<InvoiceSchedule />} />
          <Route path="/invoices/reconciliation" element={<BankReconciliation />} />
          <Route path="/invoices/cashflow" element={<Cashflow />} />
          <Route path="/invoices/received" element={<ReceivedInvoices />} />
          <Route path="/richieste" element={<PMRequests />} />
          <Route path="/invoices/new" element={<InvoiceForm />} />
          <Route path="/invoices/:id" element={<InvoiceDetail />} />
          <Route path="/invoices/:id/edit" element={<InvoiceForm />} />

          <Route path="/profile" element={<Profile />} />

          <Route path="/admin/users" element={<UsersAdmin />} />
          <Route path="/admin/tax" element={<TaxSettings />} />
          <Route path="/admin/studio" element={<StudioSettings />} />
          <Route path="/admin/audit-log" element={<AuditLog />} />
          <Route path="/admin/data-import" element={<DataImport />} />
          <Route path="/admin/team-stats" element={<TeamStats />} />
          <Route path="/admin/line-templates" element={<LineTemplates />} />
          <Route path="/admin/payment-terms" element={<PaymentTerms />} />
          <Route path="/admin/calendario-google" element={<GoogleCalendar />} />
          <Route path="/admin/notifiche" element={<Notifications />} />

          {/* Controllo di Gestione: rotte aggiunte solo se il backend
              dichiara CONTROLLING_ENABLED=true. Lazy-loaded. */}
          <Route
            path="/controlling/*"
            element={
              <Suspense fallback={<Loading />}>
                <ControllingRouter />
              </Suspense>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
    </DialogProvider>
    </ToastProvider>
  )
}

function ControllingRouter() {
  const enabled = useFeaturesStore((s) => s.controllingEnabled)
  if (!enabled) return <Navigate to="/" replace />
  return <Routes>{controllingRoutes()}</Routes>
}

function DashboardSwitch() {
  const { user } = useAuthStore()
  let dash
  if (!canSeeCompanyDashboard(user)) dash = <MyDashboard />
  else if (isManagerUser(user)) dash = <PMDashboard />
  else dash = <Dashboard />
  return (
    <>
      <OnboardingBanner />
      {dash}
    </>
  )
}
